import {
  Button,
  Table,
  Modal,
  DeleteModal,
} from "@telluscompany/design-system";
import { useSearchParams } from "react-router-dom";
import { useCrudModals } from "@shared/hooks";
import { Gs3TablePaginator } from "@shared/components";
import { EMPTY_PAGINATION_META } from "@shared/constants/pagination.constants";
import { UserFormData } from "../../schemas";
import { UsersFilter } from "./UsersFilter";
import { UsersActionMenu } from "./UsersActionMenu";
import { CreateUserForm } from "./CreateUserForm";
import { EditUserForm } from "./EditUserForm";
import { PlusCircle } from "lucide-react";
import { useLocalization } from "@i18n";
import {
  ListUsersManagerialResponse,
  useCreateUserMutation,
} from "../../api/user";
import { PaginationMeta } from "@shared/api";
import {
  useDeleteUserMutation,
  useEditUserMutation,
} from "../../api/user/users.mutations";
import { useUserContext } from "@/shared/contexts";
import { useUpdateTableUserPreferences } from "@shared/hooks";

interface UsersTabProps {
  users: ListUsersManagerialResponse[];
  isLoading?: boolean;
  onFilter: (filters: Record<string, unknown>) => void;
  onClearFilter: () => void;
  initialFilters?: Record<string, string>;
  paginatedMeta?: PaginationMeta;
}

const UsersTab = ({
  users,
  onFilter,
  onClearFilter,
  initialFilters,
  paginatedMeta,
}: UsersTabProps) => {
  const { translate } = useLocalization();
  const {
    modals,
    selectedItem: selectedUser,
    openCreate,
    openEdit,
    openDelete,
    closeCreate,
    closeEdit,
    closeDelete,
  } = useCrudModals<ListUsersManagerialResponse>();

  const { mutate: createUser } = useCreateUserMutation();
  const { mutate: editUser } = useEditUserMutation();
  const { mutate: deleteUser, isPending: isDeleting } = useDeleteUserMutation();


  const { user } = useUserContext();
  const updateTableUserPreferences = useUpdateTableUserPreferences(user?.userPreferences);

  const handleCreateUser = (data: UserFormData) => {
    createUser(
      {
        ...data,
        department: data.department ?? null,
        position: data.position ?? null,
      },
      {
        onSuccess: () => closeCreate(),
      },
    );
  };

  const handleEditUser = (data: UserFormData) => {
    if (!selectedUser) return;

    editUser(
      {
        ...data,
        id: selectedUser.id,
        department: data.department ?? null,
        position: data.position ?? null,
      },
      {
        onSuccess: () => closeEdit(),
        onError: () => closeEdit(),
      },
    );
  };

  const handleDeleteUser = () => {
    const id = selectedUser?.id;

    if (!id) return;

    deleteUser(id, {
      onSuccess: () => closeDelete(),
      onError: () => closeDelete(),
    });
  };

  const createActionMenu = (rowData: {
    rowData: ListUsersManagerialResponse;
  }) => {
    const user = rowData.rowData;

    return (
      <UsersActionMenu
        user={user}
        onEdit={() => openEdit(user)}
        onDelete={() => openDelete(user)}
      />
    );
  };

  return (
    <div className="p-4">
      <Table<ListUsersManagerialResponse>
        userId="USER1"
        tableId="users-table"
        usersPreferencesFromServer={user?.userPreferences}
        updateUsersPreferencesOnServer={updateTableUserPreferences}
        useSearchParamsHook={useSearchParams}
        enableSortByUrl
        TableActionMenu={ user?.role === "owner" ? createActionMenu as React.ComponentType : undefined}
        TableButtons={
          user?.role === "owner" && (
          <>
            <Button
              size="small"
              variant="primary"
              onClick={openCreate}
              iconLeft={<PlusCircle />}
            >
              {translate("management.users.button.new")}
            </Button>
            </>
          )
        }
        TableFilter={() => (
          <UsersFilter
            onFilter={onFilter}
            onClear={onClearFilter}
            initialFilters={initialFilters}
          />
        )}
        TablePaginator={
          <Gs3TablePaginator
            meta={paginatedMeta ?? EMPTY_PAGINATION_META}
            useUrlState
            useSearchParamsHook={useSearchParams}
          />
        }
        draggable
        enableTableColumnsSelector
        filterButtonText={translate("management.users.table.filterButtonText")}
        handleClickRow={() => {}}
        tableColumns={[
          {
            key: "name",
            label: translate("management.users.table.column.name"),
            minWidth: "60px",
            sortType: "text",
          },
          {
            key: "email",
            label: translate("management.users.table.column.email"),
            minWidth: "150px",
            sortType: "text",
          },
          {
            key: "role",
            label: translate("management.users.table.column.role"),
            minWidth: "200px",
            sortType: "text",
          },
        ]}
        tableData={users.map(
          (user) => ({ ...user }) as { [key: string]: unknown; color?: string },
        )}
      />

      <Modal
        open={modals.create}
        content={
          <CreateUserForm onSubmit={handleCreateUser} onCancel={closeCreate} />
        }
        onClose={closeCreate}
        title={translate("management.users.modal.title.create")}
      />

      <Modal
        open={modals.edit}
        content={
          <EditUserForm
            onSubmit={handleEditUser}
            onCancel={closeEdit}
            userId={selectedUser?.id ?? undefined}
          />
        }
        onClose={closeEdit}
        title={translate("management.users.modal.title.edit")}
      />

      <DeleteModal
        open={modals.delete}
        onClose={closeDelete}
        onConfirm={handleDeleteUser}
        isDeleting={isDeleting}
        itemDescription={selectedUser?.name}
        title={translate("common.deleteModalTitle")}
        message={translate("common.deleteModalItemMessage")}
        alertMessage={translate("common.deleteModalAlertMessage")}
        confirmButtonText={translate("common.delete")}
        cancelButtonText={translate("common.cancel")}
        deletingButtonText={translate("common.deleting")}
      />
    </div>
  );
};

export default UsersTab;
